import { CURRENCIES_PRECISIONS } from '@config/constants/currencies'
import logger from '@logger'

// This is temporary to get time to find and fix calls to 'getCurrencyPrecision' that
// does not pass a valid currency.
// 2 is the precision of currencies managed so far (EUR, USD…). This fallback should be
// removed before JP launch that uses JPY currency (or any currency not having
// a precision of 2.
const CURRENCY_PRECISION_FALLBACK = 2

export const getCurrencyPrecision = (currency) => {
  const precision = CURRENCIES_PRECISIONS[currency]

  if (precision === undefined) {
    logger.error(
      new Error(
        `Missing currency precision config for '${currency}'. Fallback to ${CURRENCY_PRECISION_FALLBACK}`,
      ),
    )

    // TODO [APAC-314]
    // After fixing issues detected thanks to logs above. Remove the logs and replace 'return FALLBACK' with:
    // throw new Error(`Missing currency precision config for '${currency}'`)
    return CURRENCY_PRECISION_FALLBACK
  }

  return precision
}

export const getCurrencyPrecisionAsDecimal = (currency) => {
  return 10 ** -getCurrencyPrecision(currency)
}

/**
 * Convert an unit amount to a subunit amount.
 * This helper is made to avoid some calculations errors due to JavaScript.
 * For example: 160.9 * 100 = 16090 but 160.8 * 100 = 16080.0000000002.
 * So instead, split the integer and the decimal part (by converting the amount
 * to string), multiply the integer part by 100 and the decimal part by 10 and
 * add them together.
 * The 100 factor actually depends on the currency precision. For example, the
 * factor is 1 for currencies without decimals.
 *
 * @param  {number} amount The amount to convert into subunits.
 * @return {number} The amount converted to subunits.
 *
 * @throws When the provided amount is not a number (integer or float).
 */
export const unitsToSubunits = (units, currency) => {
  if (typeof units !== 'number') {
    throw new Error(
      `You should provide a numeric amount (${units}, ${typeof units} given)!`,
    )
  }

  const strUnits = String(units)
  const [integer = '0', decimal = '0'] = strUnits.split('.')

  const precision = getCurrencyPrecision(currency)

  let result = parseInt(integer, 10) * 10 ** precision

  for (let i = 1; i <= Math.min(precision, decimal.length); i += 1) {
    result += parseInt(decimal.charAt(i - 1), 10) * 10 ** (precision - i)
  }

  return result
}

export const subunitsToUnits = (subunits, currency) => {
  if (typeof subunits !== 'number') {
    throw new Error(
      `You should provide a numeric subunits amount (${subunits}, ${typeof subunits} given)!`,
    )
  }

  return subunits / 10 ** getCurrencyPrecision(currency)
}
